import { isNegative, isPositive, isZero, minus, sumMoney, type Money } from "../money/exact";

/**
 * The mobile-money wallets a sale could be paid with, by name.
 *
 * The till asked which one until 2026-10-08, when the owner dropped the
 * question: a sale is now simply paid (`PAID`), on credit (`DEYN`), or part
 * of each. The names stay because older sales and payments carry them, and
 * because a phone still on an older version of the app may send them — the
 * server keeps accepting them and counts them as money received.
 *
 * A subset of `PAYMENT_METHODS` in the database package, which keeps every
 * method the schema has ever known.
 */
export const WALLETS = ["evcplus", "edahab", "zaad", "jeeb", "sahal", "premier"] as const;

export type Wallet = (typeof WALLETS)[number];

export function isWallet(value: string): value is Wallet {
  return (WALLETS as readonly string[]).includes(value);
}

/**
 * Credit — deyn: the customer takes the medicine now and pays later. Paid
 * "by" deyn beside, or instead of, a wallet; no money came in, so it is not a
 * wallet, and the sale goes on the customer's balance. The same word the
 * database has always used for it.
 */
export const DEYN = "deyn" as const;

/**
 * Money that came in, without saying how: what the till records since it
 * stopped asking for the wallet.
 */
export const PAID = "paid" as const;

/** Everything a sale or a payment may be recorded with: the old wallets, paid, and deyn. */
export const PAY_METHODS = [...WALLETS, PAID, DEYN] as const;

export type PayMethod = (typeof PAY_METHODS)[number];

export function isPayMethod(value: string): value is PayMethod {
  return (PAY_METHODS as readonly string[]).includes(value);
}

/** How much of a sale went on credit. */
export function creditOf(payments: ReadonlyArray<{ method: string; amount: Money }>): Money {
  return sumMoney(payments.filter((p) => p.method === DEYN).map((p) => p.amount));
}

/** How much of a sale was paid there and then: everything that is not credit. */
export function paidOf(payments: ReadonlyArray<{ method: string; amount: Money }>): Money {
  return sumMoney(payments.filter((p) => p.method !== DEYN).map((p) => p.amount));
}

/**
 * The payments a sale is recorded with, from the review page's two answers.
 *
 * Credit off: the whole total is paid. Credit on: what was paid now — nothing,
 * or part — and the rest on credit. Credit with all of it paid now is not
 * credit, so it is refused: null, like a paid-now amount below zero. A sale
 * that comes to nothing has nothing to pay.
 */
export function paymentsFor(
  total: Money,
  credit: { paidNow: Money } | null,
): Array<{ method: typeof PAID | typeof DEYN; amount: Money }> | null {
  if (isZero(total)) return [];
  if (credit === null) return [{ method: PAID, amount: total }];
  const owed = minus(total, credit.paidNow);
  if (!isPositive(owed) || isNegative(credit.paidNow)) return null;
  return [
    ...(isPositive(credit.paidNow) ? [{ method: PAID, amount: credit.paidNow }] : []),
    { method: DEYN, amount: owed },
  ];
}

/**
 * The payments a sale is recorded with, from how much of it is on credit — the
 * box that is always on the receipt being checked (the owner, 2026-10-18).
 *
 * Nothing on credit: the whole total is paid. Some: the rest is paid now. All
 * of it: nothing paid now. Below zero, or more than the total: null, refused.
 */
export function paymentsWithCredit(
  total: Money,
  credit: Money,
): Array<{ method: typeof PAID | typeof DEYN; amount: Money }> | null {
  if (isNegative(credit)) return null;
  const paidNow = minus(total, credit);
  if (isNegative(paidNow)) return null;
  if (isZero(total)) return [];
  return [
    ...(isPositive(paidNow) ? [{ method: PAID, amount: paidNow }] : []),
    ...(isPositive(credit) ? [{ method: DEYN, amount: credit }] : []),
  ];
}
